//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { Contract, Signer } from 'ethers'

import { ActivityEnum, AlertLevelEnum, PoolTypeEnum, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { IPoolBaseAbi, PoolControllerDynamicAbi, PoolDynamicAbi } from '@shared/Abis'

import { dispatchError, OtStoreState } from '@react/redux/store'

import { otAssert } from '@shared/conversions'

import { alertActions } from '@react/redux/alertSlice'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { PoolControllerDynamic, PoolDynamic } from '@typechain-types/index'
import { UserConnection } from '@shared/DeploymentConfigTypes'

export const dynamicSetLinearExchangeRateTx =
  (props: {
    poolAddr: EthAddr
    exchangeRate: string
    exchangeRateChangeRate: string
    interestRate: number
  }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const { poolAddr, exchangeRate, exchangeRateChangeRate, interestRate } = props
    const activity = ActivityEnum.POOL_ACTIVATE
    try {
      logOT.debug(activity)
      const { webSocket } = getState().chainState
      const { chainConfigurationName } = getState().chainState.chainConfiguration
      const { roleEnum, signer } = getState().chainState.userConnection as UserConnection
      otAssert(
        roleEnum == RoleEnum.POOL_ADMIN ||
          roleEnum == RoleEnum.BORROWER ||
          roleEnum == RoleEnum.ISSUER ||
          roleEnum == RoleEnum.PROTOCOL_MANAGER
      )
      otAssert(signer, 'signer is not defined')

      await dynamicSetLinearExchangeRate(
        webSocket,
        signer as Signer,
        poolAddr,
        exchangeRate,
        exchangeRateChangeRate,
        interestRate,
        chainConfigurationName
      )

      dispatch(poolActions.setExchangeRateSuccess({ poolAddr }))
    } catch (e: any) {
      dispatch(poolActions.setExchangeRateFail({}))
      dispatch(
        alertActions.addAlert({
          title: 'Could not update exchange rate.',

          description: 'Please wait and try again',
          createdAt: Date.now() / 1000,
          dueDateTime: Date.now() / 1000,
          txHash: '',
          sourceObjectAddr: ETH_ADDR_ZERO,
          alertLevel: AlertLevelEnum.ERROR,
        })
      )
      await dispatchError(dispatch, e, activity, 1037)
    }
  }
const dynamicSetLinearExchangeRate = async (
  webSocket: WebSocket | undefined,
  poolAdminSigner: Signer,
  poolAddr: EthAddr,
  exchangeRate: string,
  exchangeRateChangeRate: string,
  interestRate: number,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug('activatePool ')
  const uuid = txUUID()
  // TODO: Double check this is correct (it was pool_active, which doesn't make sense in the 1st place. Check for how this is used backend
  webSocket?.send(
    wsServerActionPoolTXRequest(ActivityEnum.EXCHANGE_RATE_UPDATE, poolAddr, uuid, chainConfigurationName)
  )

  const poolBaseContract = new Contract(poolAddr, IPoolBaseAbi, poolAdminSigner)
  const poolType = Number(await poolBaseContract.poolType())

  if (poolType === PoolTypeEnum.Dynamic) {
    const poolContract = new Contract(poolAddr, PoolDynamicAbi, poolAdminSigner) as unknown as PoolDynamic
    const poolControllerAddr = await poolContract.poolControllerDynamic()

    otAssert(poolControllerAddr)

    const poolControllerContract = new Contract(
      poolControllerAddr,
      PoolControllerDynamicAbi,
      poolAdminSigner
    ) as unknown as PoolControllerDynamic
    const interestRateSting = Math.round(interestRate * 1e4).toString()

    const tx2 = await poolControllerContract
      .connect(poolAdminSigner)
      .setExchangeRateLinear(exchangeRate, exchangeRateChangeRate, interestRateSting)

    webSocket?.send(
      wsServerActionPoolTX(
        ActivityEnum.EXCHANGE_RATE_UPDATE,
        poolAddr,
        uuid,
        tx2.hash as TxHash,
        chainConfigurationName
      )
    )

    await tx2.wait(1)
  } else {
    throw new Error('Unknown pool type')
  }
}
